import { useMemo, useState } from "react";
import { MarketingLayout } from "@/layouts/MarketingLayout";
import { AppStoreLinks } from "@/components/AppStoreLinks";
import { EVENTS, trackEvent } from "@/lib/posthog";
import {
  MessageCircle,
  Clock,
  CalendarCheck,
  Bell,
  Smartphone,
  Sparkles,
  Check,
  ArrowRight,
  Scissors,
  Stethoscope,
  GraduationCap,
  Building2,
  ShieldCheck,
  Zap,
  Calculator,
  Sun,
  Hand,
  Repeat,
  HelpCircle,
  DoorOpen,
} from "lucide-react";

interface LandingProps {
  appUrl: string;
  /**
   * Número real de negocios usando Tippy (ej. "120", "+200").
   * Pásalo desde MarketingController para mantenerlo actualizado.
   * Si no se pasa, se usa BUSINESS_COUNT.
   */
  businessCount?: string;
  demoWhatsappNumber?: string | null;
}

const DEMO_URL = "https://calendar.app.google/bW4RkmdH62MHexxe9";

// TODO(emiliano): reemplaza con tu número real de negocios activos.
// Mejor aún: pásalo como prop `businessCount` desde MarketingController.
const BUSINESS_COUNT = "+15";

// ROI defaults (ver docs/biz/roi-framework.md)
const ROI = {
  citasNuevas: 10, // citas capturadas/mes (default pre-cuenta)
  confirmaciones: 80, // confirmaciones de recordatorio/mes
  noShowRate: 0.25, // tasa de no-show de industria
  precioPlan: 899, // plan Básico MXN
};

function SignUpButton({
  appUrl,
  label = "Comenzar gratis",
  variant = "primary",
  className = "",
  location,
}: {
  appUrl: string;
  label?: string;
  variant?: "primary" | "outline";
  className?: string;
  location: string;
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-base font-semibold transition-colors";
  const styles =
    variant === "primary"
      ? "bg-foreground text-background hover:bg-foreground/90"
      : "border border-foreground bg-background text-foreground hover:bg-muted";
  const href = `${appUrl}/users/sign_up?utm_source=marketing&cta_location=${encodeURIComponent(location)}`;

  return (
    <a
      href={href}
      onClick={() => trackEvent(EVENTS.MARKETING_SIGNUP_CLICKED, { location, label, href })}
      className={`${base} ${styles} ${className}`}
    >
      <MessageCircle className="h-5 w-5" />
      {label}
    </a>
  );
}

function DemoButton({ className = "" }: { className?: string }) {
  return (
    <a
      href={DEMO_URL}
      className={`inline-flex items-center justify-center gap-2 rounded-full border border-foreground bg-background px-7 py-3 text-base font-semibold text-foreground hover:bg-muted transition-colors ${className}`}
    >
      Agendar demo
      <ArrowRight className="h-4 w-4" />
    </a>
  );
}

export default function Landing({ appUrl, businessCount, demoWhatsappNumber }: LandingProps) {
  const count = businessCount ?? BUSINESS_COUNT;
  return (
    <MarketingLayout appUrl={appUrl}>
      <HeroSection appUrl={appUrl} count={count} />
      <TrustStrip count={count} />
      <RoiSection appUrl={appUrl} />
      <HowItWorksSection appUrl={appUrl} />
      <ConversationsSection demoWhatsappNumber={demoWhatsappNumber} />
      <WhySection />
      <ComparisonSection />
      <ForWhoSection />
      <PricingSection appUrl={appUrl} />
      <CTASection appUrl={appUrl} />
    </MarketingLayout>
  );
}

function HeroSection({ appUrl, count }: { appUrl: string; count: string }) {
  return (
    <section className="pt-28 pb-16 lg:pt-36 lg:pb-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-sm font-medium text-secondary-foreground mb-6">
              <Sparkles className="h-4 w-4" />
              Asistente de WhatsApp con IA
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight leading-tight mb-6">
              Convierte cada mensaje de WhatsApp en una{" "}
              <span className="inline-block bg-primary text-primary-foreground px-2 py-1 rounded-lg">
                cita confirmada
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-xl lg:mx-0 mx-auto">
              Tippy responde, agenda y confirma tus citas por WhatsApp, 24/7. Captura
              clientes que no alcanzas a contestar y reduce los que no llegan — sin
              estar pegado al celular y sin contratar más gente.
            </p>

            {/* CTA primero: la acción principal va antes que los badges de app */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <SignUpButton appUrl={appUrl} label="Comenzar gratis" location="hero" />
              <DemoButton />
            </div>

            <p className="mt-3 text-sm text-muted-foreground flex items-center gap-2 justify-center lg:justify-start">
              <ShieldCheck className="h-4 w-4 text-foreground" />
              Sin tarjeta · 30 días del plan Básico gratis · Cancela cuando quieras
            </p>

            {/* Prueba social: conteo real de negocios */}
            <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-foreground">
              <span className="inline-flex h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-primary/20" />
              {count} negocios ya agendan con Tippy
            </div>

            {/* App badges relegados a una línea secundaria */}
            <div className="mt-6">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5 justify-center lg:justify-start">
                <Smartphone className="h-3.5 w-3.5" />
                También disponible en iOS y Android
              </p>
              <AppStoreLinks className="justify-center lg:justify-start" />
            </div>
          </div>

          {/* Muestra el producto: simulación de conversación de WhatsApp */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="absolute -top-16 right-0 z-20 w-56 rounded-2xl border border-primary/40 bg-primary px-4 py-2.5 text-xs text-primary-foreground shadow-lg lg:-right-24 lg:-top-12">
              <span className="block font-bold">Tu propio WhatsApp Business</span>
              <span className="block font-medium opacity-80">no un número compartido</span>
            </div>
            <WhatsappDemo />
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatsappDemo() {
  return (
    <div className="relative w-full max-w-sm">
      <div className="absolute -inset-6 rounded-[2.5rem] bg-primary/20 blur-2xl" />
      <div className="relative rounded-[2rem] border border-border bg-card shadow-xl overflow-hidden">
        {/* Header estilo chat */}
        <div className="flex items-center gap-3 bg-foreground px-4 py-3 text-background">
          <img
            src="/marketing/profile.webp"
            alt="Dra. Ramírez"
            className="h-9 w-9 rounded-full object-cover object-center"
          />
          <div className="leading-tight">
            <p className="text-sm font-semibold">Dra. Ramírez</p>
            <p className="text-xs text-background/70">en línea</p>
          </div>
          <MessageCircle className="ml-auto h-5 w-5 text-background/80" />
        </div>

        {/* Conversación (teaser: pregunta → listo) */}
        <div className="space-y-3 bg-muted/40 px-4 py-5">
          <ChatBubble side="in">
            Hola, ¿me agendan consulta con la Dra. para el jueves? 🩺
          </ChatBubble>
          <ChatBubble side="out">
            ¡Claro! Te agendé el jueves 10:00 am con la Dra. Ramírez ✅ Te mando un
            recordatorio un día antes.
          </ChatBubble>
          <div className="flex justify-center pt-1">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-foreground">
              <CalendarCheck className="h-3.5 w-3.5" />
              Cita confirmada y agregada a tu calendario
            </span>
          </div>
        </div>
      </div>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        Así de simple para tus clientes — mira flujos completos más abajo ↓
      </p>
    </div>
  );
}

function ChatBubble({
  side,
  children,
}: {
  side: "in" | "out";
  children: React.ReactNode;
}) {
  const out = side === "out";
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] whitespace-pre-line rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
          out
            ? "bg-primary text-primary-foreground rounded-br-sm"
            : "bg-card text-card-foreground border border-border rounded-bl-sm"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function TrustStrip({ count }: { count: string }) {
  const items = [
    { icon: Building2, label: `${count} negocios activos` },
    { icon: MessageCircle, label: "WhatsApp nativo" },
    { icon: Smartphone, label: "Sin descargar otra app" },
    { icon: ShieldCheck, label: "Precios en MXN" },
  ];
  return (
    <section className="border-y border-border bg-card/60">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <div
              key={it.label}
              className="flex items-center justify-center gap-2 text-sm font-medium text-foreground"
            >
              <it.icon className="h-4 w-4 text-muted-foreground" />
              {it.label}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoiSection({ appUrl }: { appUrl: string }) {
  const [precio, setPrecio] = useState<number>(350);

  const result = useMemo(() => {
    const p = Number.isFinite(precio) && precio > 0 ? precio : 0;
    const rescued = Math.round(ROI.confirmaciones * ROI.noShowRate); // 20
    const capturadas = ROI.citasNuevas; // 10
    const citas = capturadas + rescued; // 30
    const recuperado = citas * p;
    const roi = p > 0 ? recuperado / ROI.precioPlan : 0;
    const breakEven = p > 0 ? Math.max(1, Math.ceil(ROI.precioPlan / p)) : 0;
    return { recuperado, roi, breakEven, citas, capturadas, rescued };
  }, [precio]);

  const mxn = (n: number) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    }).format(n);

  return (
    <section id="roi" className="py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-sm font-medium text-secondary-foreground mb-4">
            <Calculator className="h-4 w-4" />
            Calculadora de retorno
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            ¿Cuánto dinero estás dejando en la mesa?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Cada mensaje sin responder y cada cliente que no llega es dinero perdido.
            Calcula cuánto podrías recuperar con Tippy.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Input */}
            <div className="p-8 border-b md:border-b-0 md:border-r border-border">
              <label
                htmlFor="precio-cita"
                className="block text-sm font-semibold text-foreground mb-2"
              >
                ¿Cuánto cobras en promedio por cita?
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <input
                  id="precio-cita"
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={Number.isNaN(precio) ? "" : precio}
                  onChange={(e) => setPrecio(parseInt(e.target.value, 10))}
                  className="w-full rounded-xl border border-border bg-background pl-8 pr-16 py-3 text-2xl font-bold text-foreground outline-none focus:border-primary"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  MXN
                </span>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {[200, 350, 500, 800].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setPrecio(v)}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
                      precio === v
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-foreground hover:bg-muted"
                    }`}
                  >
                    {mxn(v)}
                  </button>
                ))}
              </div>

              <p className="mt-6 text-xs text-muted-foreground">
                Estimación con base en promedios de negocios similares (citas
                capturadas + no-shows rescatados con recordatorios). Al crear tu cuenta,
                el panel de Tippy te muestra tus números reales.
              </p>
            </div>

            {/* Resultado */}
            <div className="p-8 bg-primary/10 flex flex-col justify-center">
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Tippy te ayuda a no perder
              </p>
              <p className="text-4xl sm:text-5xl font-bold text-foreground">
                ~{result.citas} citas
                <span className="text-lg font-semibold text-muted-foreground">
                  {" "}/ mes
                </span>
              </p>

              {/* De dónde salen esas citas */}
              <div className="mt-4 space-y-1.5 text-sm text-foreground">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-muted-foreground" />
                    Clientes nuevos capturados
                  </span>
                  <span className="font-semibold">{result.capturadas}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-muted-foreground" />
                    Citas rescatadas con recordatorios
                  </span>
                  <span className="font-semibold">{result.rescued}</span>
                </div>
              </div>

              <div className="mt-4 border-t border-border pt-4">
                <p className="text-sm text-muted-foreground">
                  Eso equivale a{" "}
                  <span className="font-bold text-foreground">
                    {mxn(result.recuperado)}/mes
                  </span>{" "}
                  en citas — un ROI de{" "}
                  <span className="font-bold text-foreground">
                    {result.roi >= 1 ? Math.round(result.roi) : result.roi.toFixed(1)}x
                  </span>{" "}
                  sobre el plan Básico ({mxn(ROI.precioPlan)}/mes).
                </p>
              </div>

              <p className="mt-4 text-base font-semibold text-foreground">
                Con rescatar solo {result.breakEven}{" "}
                {result.breakEven === 1 ? "cita" : "citas"} al mes, el plan ya se pagó
                solo.
              </p>

              <SignUpButton
                appUrl={appUrl}
                label="Empezar a recuperar citas"
                location="roi"
                className="mt-6 w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection({ appUrl }: { appUrl: string }) {
  const steps = [
    {
      number: "1",
      title: "Tus clientes escriben por WhatsApp",
      description: "Preguntan precios, horarios o disponibilidad — como siempre.",
    },
    {
      number: "2",
      title: "Tippy responde y agenda al instante",
      description:
        "Envía la información correcta, reserva la cita y la sincroniza con tu calendario.",
    },
    {
      number: "3",
      title: "Confirma y reduce los no-shows",
      description:
        "Manda recordatorios automáticos para que tus clientes sí lleguen. Tú solo atiendes.",
    },
  ];

  return (
    <section id="como-funciona" className="py-20 bg-muted/50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Cómo funciona Tippy (en 3 pasos simples)
          </h2>
          <p className="text-lg text-muted-foreground">
            Así de fácil es automatizar tus citas por WhatsApp:
          </p>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="hidden md:block absolute top-8 left-[16.67%] right-[16.67%] border-t-2 border-dashed border-border" />
          {steps.map((step) => (
            <div key={step.number} className="relative z-10">
              <div className="text-center">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-2xl font-bold mb-4">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <SignUpButton appUrl={appUrl} label="Comenzar gratis" location="how_it_works" />
          <p className="mt-3 text-sm text-muted-foreground">
            Sin tarjeta. Listo en minutos.
          </p>
        </div>
      </div>
    </section>
  );
}

type ChatMsg = { side: "in" | "out"; text: string; confirm?: boolean };

const CONVERSATIONS: Record<
  "agendar" | "preguntas" | "reagendar",
  { label: string; icon: typeof CalendarCheck; messages: ChatMsg[] }
> = {
  agendar: {
    label: "Agendar una cita",
    icon: CalendarCheck,
    messages: [
      { side: "in", text: "Hola" },
      {
        side: "out",
        text: "¡Hola! Bienvenido al consultorio de la Dra. Ramírez 🩺 ¿En qué puedo ayudarte? Si deseas agendar una cita, puedo mostrarte los servicios disponibles.",
      },
      { side: "in", text: "Quiero agendar una cita" },
      {
        side: "out",
        text: "Estos son nuestros servicios:\n1. Consulta general · 30 min · $800\n2. Consulta de seguimiento · 20 min · $500\n¿Para cuál te gustaría agendar?",
      },
      { side: "in", text: "1" },
      {
        side: "out",
        text: "La consulta general está disponible en dos consultorios:\n1. Polanco (Av. Masaryk 111)\n2. Roma (Av. Álvaro Obregón 64)\nResponde con el número que prefieras.",
      },
      { side: "in", text: "2" },
      {
        side: "out",
        text: "Perfecto, consultorio Roma. ¿Para cuándo te gustaría tu cita?",
      },
      { side: "in", text: "Jueves en la mañana" },
      {
        side: "out",
        text: "Horarios disponibles con la Dra. Ramírez el jueves:\n1. 9:00 am\n2. 9:30 am\n3. 10:00 am\n4. 11:00 am\n¿Cuál prefieres?",
      },
      { side: "in", text: "La de las 10" },
      { side: "out", text: "¿A nombre de quién es la cita?" },
      { side: "in", text: "María González" },
      {
        side: "out",
        text: "Para confirmar: consulta general, jueves 10:00 am con la Dra. Ramírez. ¿Confirmas? (sí/no)",
      },
      { side: "in", text: "Sí" },
      {
        side: "out",
        text: "¡Listo! Tu cita quedó agendada para el jueves a las 10:00 am con la Dra. Ramírez. Te enviaré un recordatorio 📅",
        confirm: true,
      },
    ],
  },
  preguntas: {
    label: "Responde preguntas",
    icon: HelpCircle,
    messages: [
      {
        side: "in",
        text: "Hola, ¿qué servicios ofrecen? 🤔",
      },
      {
        side: "out",
        text: "¡Hola! Ofrecemos consulta general ($800) y consulta de seguimiento ($500). ¿Te gustaría que te ayude a agendar? 😊",
      },
      { side: "in", text: "Sí, porfa" },
      {
        side: "out",
        text: "¡Perfecto! ¿Qué servicio te gustaría agendar?",
      },
    ],
  },
  reagendar: {
    label: "Reagendar sin perder al cliente",
    icon: Repeat,
    messages: [
      {
        side: "out",
        text: "Hola María 👋 Te recordamos tu cita mañana jueves a las 10:00 am con la Dra. Ramírez. ¿Confirmas tu asistencia?",
      },
      { side: "in", text: "Uy, mañana no voy a poder 😕 ¿hay algo el viernes?" },
      {
        side: "out",
        text: "¡Sin problema! El viernes la Dra. Ramírez tiene:\n• 9:00 am\n• 11:30 am\n• 1:00 pm\n¿Cuál te queda mejor?",
      },
      { side: "in", text: "11:30 porfa" },
      {
        side: "out",
        text: "Listo ✅ Moví tu cita al viernes 11:30 am. ¡Te esperamos!",
        confirm: true,
      },
    ],
  },
};

function ConversationsSection({ demoWhatsappNumber }: { demoWhatsappNumber?: string | null }) {
  const [tab, setTab] = useState<"agendar" | "preguntas" | "reagendar">("agendar");
  const active = CONVERSATIONS[tab];
  const cleanDemoNumber = demoWhatsappNumber?.replace(/\D/g, "");
  const demoWhatsappUrl = cleanDemoNumber
    ? `https://wa.me/${cleanDemoNumber}?text=${encodeURIComponent("Hola, quiero agendar una cita")}`
    : null;

  return (
    <section className="py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Mira a Tippy en acción
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Conversaciones reales por WhatsApp: agenda, confirma y reagenda sola — y a ti
            te mantiene al tanto de todo.
          </p>
          {demoWhatsappUrl && (
            <div className="mt-6 flex flex-col items-center gap-2">
              <a
                href={demoWhatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent(EVENTS.MARKETING_DEMO_CLICKED, { location: "conversations_section", href: demoWhatsappUrl })}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
              >
                <MessageCircle className="h-5 w-5" />
                Probar demo por WhatsApp
              </a>
              <p className="text-xs text-muted-foreground">
                Demo con datos ficticios de la Dra. Ramírez. No agenda citas reales.
              </p>
            </div>
          )}
        </div>

        {/* Selector de ejemplo */}
        <div className="flex flex-col sm:flex-row justify-center gap-3 mb-8">
          {(Object.keys(CONVERSATIONS) as Array<keyof typeof CONVERSATIONS>).map(
            (key) => {
              const conv = CONVERSATIONS[key];
              const isActive = key === tab;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors ${
                    isActive
                      ? "bg-foreground text-background border-foreground"
                      : "border-border text-foreground hover:bg-muted"
                  }`}
                >
                  <conv.icon className="h-4 w-4" />
                  {conv.label}
                </button>
              );
            },
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Conversación */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative w-full max-w-sm">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-primary/20 blur-2xl" />
              <div className="relative rounded-[2rem] border border-border bg-card shadow-xl overflow-hidden">
                <div className="flex items-center gap-3 bg-foreground px-4 py-3 text-background">
                  <img
                    src="/marketing/profile.webp"
                    alt="Dra. Ramírez"
                    className="h-9 w-9 rounded-full object-cover object-center"
                  />
                  <div className="leading-tight">
                    <p className="text-sm font-semibold">
                      Dra. Ramírez
                    </p>
                    <p className="text-xs text-background/70">en línea</p>
                  </div>
                  <MessageCircle className="ml-auto h-5 w-5 text-background/80" />
                </div>
                <div className="space-y-3 bg-muted/40 px-4 py-5 max-h-[28rem] overflow-y-auto">
                  {active.messages.map((m, i) => (
                    <div key={i}>
                      <ChatBubble side={m.side}>{m.text}</ChatBubble>
                      {m.confirm && (
                        <div className="flex justify-center pt-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-foreground">
                            <CalendarCheck className="h-3.5 w-3.5" />
                            Agregada a tu calendario
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Notificaciones para el dueño */}
          <div className="lg:pt-4">
            <h3 className="text-2xl font-bold text-foreground mb-2">
              Y tú te enteras de todo
            </h3>
            <p className="text-muted-foreground mb-6">
              Mientras Tippy atiende, tú recibes notificaciones en tu celular. Sin abrir
              WhatsApp ni revisar la agenda a cada rato.
            </p>
            <div className="space-y-3">
              <OwnerNotification
                icon={Bell}
                title="Nueva cita"
                body="María González — Consulta general · jue 10:00 am · Roma"
              />
              <OwnerNotification
                icon={Sun}
                title="Hoy hay 5 citas"
                body="Primera a las 9:00 am"
              />
              <OwnerNotification
                icon={Hand}
                title="Un cliente necesita atención"
                body="Tippy te avisa cuando alguien pide hablar con una persona."
              />
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Notificaciones reales que recibe el dueño en la app de Tippy (iOS y
              Android).
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function OwnerNotification({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof Bell;
  title: string;
  body: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border bg-card px-4 py-3 shadow-sm">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/15 text-foreground">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-bold text-background bg-foreground rounded px-1.5 text-xs py-0.5">
            Tippy
          </span>
          <p className="text-sm font-semibold text-foreground truncate">{title}</p>
        </div>
        <p className="text-sm text-muted-foreground mt-0.5">{body}</p>
      </div>
    </div>
  );
}

function WhySection() {
  const benefits = [
    {
      icon: Zap,
      title: "Captura más clientes",
      description:
        "Responde al instante, 24/7. No pierdes la cita por tardar en contestar.",
    },
    {
      icon: Bell,
      title: "Rescata las que se perderían",
      description:
        "Recordatorios automáticos que reducen los clientes que no llegan.",
    },
    {
      icon: CalendarCheck,
      title: "Agenda sincronizada",
      description:
        "Las citas se reservan solas y se bloquean en tu calendario, sin idas y vueltas.",
    },
    {
      icon: DoorOpen,
      title: "Nunca dupliques una sala o equipo",
      description:
        "Tippy considera servicios, personal y espacios (salas, cabinas, láser, exilis) para que la agenda y el bot de WhatsApp nunca empalmen dos citas en la misma máquina.",
    },
    {
      icon: Smartphone,
      title: "Control desde tu celular",
      description:
        "Gestiona agenda, servicios y equipo desde la app, estés donde estés.",
    },
    {
      icon: Clock,
      title: "Sin contratar más gente",
      description:
        "Mejora la atención sin sumar un recepcionista a tu nómina.",
    },
  ];

  return (
    <section className="py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Atender bien no debería quitarte tiempo
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Tippy trabaja en los dos momentos donde se pierde dinero: cuando un cliente
            escribe y nadie responde, y cuando agenda pero no llega.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/15 text-foreground mb-4">
                <benefit.icon className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold text-card-foreground mb-2">
                {benefit.title}
              </h3>
              <p className="text-sm text-muted-foreground">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  return (
    <section className="py-20 bg-muted/50">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            ¿Ya usas otras herramientas? Tippy las complementa
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            No tienes que cambiar cómo te encuentran tus clientes. Tippy se encarga de
            la administración del día a día.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Directorios (ej. Doctoralia)
            </p>
            <p className="text-xl font-bold text-foreground mb-2">Te dan visibilidad</p>
            <p className="text-sm text-muted-foreground">
              Te ayudan a que nuevos clientes te encuentren.
            </p>
          </div>
          <div className="rounded-2xl border-2 border-foreground bg-primary/10 p-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-foreground/70 mb-2">
              Tippy
            </p>
            <p className="text-xl font-bold text-foreground mb-2">
              Te da administración
            </p>
            <p className="text-sm text-foreground">
              Responde, agenda y confirma por WhatsApp — donde ya te escriben tus
              clientes, sin que descarguen otra app.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ForWhoSection() {
  const audiences = [
    { icon: Scissors, title: "Salones de belleza, barberías y spas" },
    { icon: Stethoscope, title: "Clínicas, consultorios y centros de salud" },
    { icon: GraduationCap, title: "Estudios, academias y servicios especializados" },
    { icon: Building2, title: "Cualquier negocio que agenda citas por WhatsApp" },
  ];

  return (
    <section id="para-quien" className="py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            ¿Para quién es Tippy?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Si tu negocio vende con tu tiempo y tus clientes te escriben por WhatsApp,
            Tippy es para ti. Pensado para negocios de servicios que reciben mensajes
            todos los días.
          </p>
          <p className="text-sm font-semibold text-foreground uppercase tracking-wide mt-6">
            Ideal para:
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {audiences.map((audience) => (
            <div
              key={audience.title}
              className="bg-card border border-border rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-foreground mx-auto mb-4">
                <audience.icon className="h-7 w-7" />
              </div>
              <h3 className="text-base font-semibold text-card-foreground">
                {audience.title}
              </h3>
            </div>
          ))}
        </div>

        <p className="text-center text-muted-foreground mt-8">
          Si tus clientes preguntan lo mismo una y otra vez, Tippy lo resuelve
          automáticamente.
        </p>
      </div>
    </section>
  );
}

function PricingSection({ appUrl }: { appUrl: string }) {
  const plans = [
    {
      name: "Gratis",
      subtitle: "Empieza sin fricción",
      price: "MX$0",
      cadence: "/ mes",
      description: "Calendario y servicios para organizar tu agenda. Para siempre.",
      features: [
        "1 negocio / 1 sucursal",
        "1 staff",
        "Servicios ilimitados",
        "Calendario + agenda manual",
        "Página de reservas básica",
      ],
      note: "Sin automatización de WhatsApp. Ideal para organizar tu agenda primero.",
      ctaLabel: "Empezar gratis",
      highlighted: false,
      order: "order-2 md:order-1",
    },
    {
      name: "Básico",
      subtitle: "Solopreneur",
      price: "MX$899",
      cadence: "/ mes",
      description:
        "Para profesionales independientes. El agente de IA responde y agenda por ti.",
      features: [
        "Todo lo del plan Gratis",
        "2 staff",
        "1 número de WhatsApp",
        "Agente de WhatsApp con IA",
        "Recordatorios automáticos",
        "400 conversaciones/mes",
      ],
      note: "30 días gratis, sin tarjeta. La opción más elegida.",
      ctaLabel: "Probar 30 días gratis",
      highlighted: true,
      order: "order-1 md:order-2",
    },
    {
      name: "Pro",
      subtitle: "Negocio en crecimiento",
      price: "MX$1,490",
      cadence: "/ mes",
      description: "Para consultorios, spas y salones con más volumen y sucursales.",
      features: [
        "Todo lo del plan Básico",
        "Hasta 3 sucursales",
        "Staff ilimitado",
        "Espacios (salas y equipos) sin empalmes",
        "Recordatorios avanzados",
        "Panel + métricas",
        "600 conversaciones/mes",
        "Soporte prioritario",
      ],
      note: "Más citas, menos carga operativa.",
      ctaLabel: "Comenzar gratis",
      highlighted: false,
      order: "order-3 md:order-3",
    },
  ];

  const faqs = [
    {
      question: "¿Qué cuenta como “conversación”?",
      answer: "Una conversación es una ventana de 24h de WhatsApp con un cliente.",
    },
    {
      question: "¿Qué pasa si me paso de mis conversaciones?",
      answer:
        "Nada se rompe. Puedes agregar un paquete de +300 conversaciones por MX$490.",
    },
    {
      question: "¿Cómo se cuenta el mes?",
      answer: "El periodo es rolling y empieza desde tu fecha de alta.",
    },
    {
      question: "¿Puedo cancelar cuando quiera?",
      answer: "Sí, el plan es mensual y cancelable en cualquier momento.",
    },
    {
      question: "¿Esto reemplaza a mi recepcionista?",
      answer:
        "Reduce la carga y automatiza lo repetitivo. Tú decides cuándo entra una persona.",
    },
    {
      question: "¿Necesito tarjeta de crédito para la prueba gratis?",
      answer:
        "No. Solo regístrate y activa el plan Básico. Sin tarjeta, sin compromiso, 30 días.",
    },
  ];

  return (
    <section id="planes" className="py-20 bg-muted/50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm uppercase tracking-wide text-muted-foreground mb-3">
            Planes de Tippy
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            Menos que un recepcionista. Más citas agendadas.
          </h2>
          <p className="text-lg text-muted-foreground">
            Prueba el plan Básico gratis por 30 días — sin tarjeta.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl p-8 flex flex-col ${plan.order} ${
                plan.highlighted
                  ? "bg-card border-2 border-foreground shadow-xl md:scale-[1.03]"
                  : "bg-card border border-border"
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs font-semibold px-3 py-1 rounded-full">
                  Recomendado
                </div>
              )}

              <div className="mb-5">
                <h3 className="text-2xl font-bold mb-1 text-foreground">
                  Plan {plan.name}
                </h3>
                <p className="text-sm font-medium text-muted-foreground">
                  {plan.subtitle}
                </p>
              </div>

              <div className="mb-5 flex items-end gap-2">
                <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                <span className="text-sm text-muted-foreground">{plan.cadence}</span>
              </div>

              <p className="text-sm mb-6 text-muted-foreground">{plan.description}</p>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check className="h-5 w-5 flex-shrink-0 mt-0.5 text-foreground" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto">
                <p className="text-sm italic mb-6 text-muted-foreground">{plan.note}</p>
                <a
                  href={`${appUrl}/users/sign_up?utm_source=marketing&cta_location=pricing_${plan.name.toLowerCase()}`}
                  onClick={() => trackEvent(EVENTS.MARKETING_SIGNUP_CLICKED, {
                    location: `pricing_${plan.name.toLowerCase()}`,
                    label: plan.ctaLabel,
                    plan: plan.name,
                    href: `${appUrl}/users/sign_up?utm_source=marketing&cta_location=pricing_${plan.name.toLowerCase()}`,
                  })}
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold w-full transition-colors ${
                    plan.highlighted
                      ? "bg-foreground text-background hover:bg-foreground/90"
                      : "border border-foreground text-foreground hover:bg-muted"
                  }`}
                >
                  {plan.ctaLabel}
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-secondary p-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Uso justo, sin sorpresas
          </h3>
          <p className="text-sm text-muted-foreground">
            La mayoría de negocios nunca exceden su volumen incluido. El periodo es
            rolling desde tu fecha de alta.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-foreground">
            <span className="font-semibold">¿Necesitas más conversaciones?</span>
            <span>Paquete +300 conversaciones por MX$490.</span>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Nota: “Conversación” = una ventana de 24h de WhatsApp con un cliente.
          </p>
        </div>

        <div className="mt-12">
          <h3 className="text-2xl font-semibold text-foreground mb-6 text-center">
            Preguntas frecuentes de precios
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="rounded-xl border border-border bg-card p-6"
              >
                <h4 className="text-base font-semibold text-foreground mb-2">
                  {faq.question}
                </h4>
                <p className="text-sm text-muted-foreground">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection({ appUrl }: { appUrl: string }) {
  return (
    <section id="cta" className="py-20 bg-primary">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-primary-foreground mb-4">
            ¿Listo para llenar tu agenda?
          </h2>
          <p className="text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Empieza gratis hoy y deja que Tippy responda, agende y confirme tus citas
            por ti.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`${appUrl}/users/sign_up?utm_source=marketing&cta_location=final_cta`}
              onClick={() => trackEvent(EVENTS.MARKETING_SIGNUP_CLICKED, {
                location: "final_cta",
                label: "Comenzar gratis",
                href: `${appUrl}/users/sign_up?utm_source=marketing&cta_location=final_cta`,
              })}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-7 py-3 text-base font-semibold text-background hover:bg-foreground/90 transition-colors"
            >
              <MessageCircle className="h-5 w-5" />
              Comenzar gratis
            </a>
            <a
              href={DEMO_URL}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-foreground bg-background px-7 py-3 text-base font-semibold text-foreground hover:bg-muted transition-colors"
            >
              Agendar demo
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <p className="mt-4 text-sm text-primary-foreground/80">
            Sin tarjeta · 30 días gratis · Cancela cuando quieras
          </p>
        </div>
      </div>
    </section>
  );
}
